export const ORDER_STATUSES = [
    'PENDING',
    'ACCEPTED',
    'PICKING',
    'READY',
    'COMPLETED',
    'CANCELLED',
    'REFUNDED',
];
export const ORDER_CHANNELS = ['CUSTOMER', 'STAFF'];
export const ORDER_LINE_SOURCES = ['PRODUCT', 'PROMOTION'];
/**
 * 订单状态机合法迁移表（`01-requirements.md` §6.2）。
 * 键为当前状态，值为允许迁移到的状态集合。
 *
 * `ACCEPTED → READY` 是**链 D 品质审查 I-1 补上的边**：全部行已覆盖（挑拣或标记缺货）
 * 且**已收款**时不必先经 `PICKING` —— 收款与挑拣是两条并行的门，谁最后完成谁负责推进，
 * 而「最后完成的是收款」在缺货场景下是**常态**（Phase 5 的 `/short` 一上线，店员标完
 * 缺货 → 收款 → 直接 `READY`，中间一次挑拣动作都没有）。
 * 判据与 `PICKING → READY` **完全同一个**（`canReachReady`），因此这条边不是新语义，
 * 而是把 `OrderStateService` 早已实现（且被 Task 15 的 e2e 钉住）的跃迁补进表里 ——
 * 表与实现不一致时，`canTransitionOrder` 的读方（收银端按钮、Phase 5 的守卫）
 * 会先行拒绝一个服务端允许的跃迁。
 */
export const ORDER_STATUS_TRANSITIONS = {
    PENDING: ['ACCEPTED', 'CANCELLED'],
    ACCEPTED: ['PICKING', 'READY', 'CANCELLED'],
    PICKING: ['READY', 'CANCELLED'],
    READY: ['COMPLETED', 'CANCELLED'],
    COMPLETED: ['REFUNDED'],
    CANCELLED: [],
    REFUNDED: [],
};
/** 判断订单状态迁移是否合法 */
export function canTransitionOrder(from, to) {
    return ORDER_STATUS_TRANSITIONS[from].includes(to);
}
/**
 * **已关闭集合（禁改）**（= 货架禁改、`PRODUCT_IN_USE` 等判据里的「未完成订单」反向）。
 * 措辞不是「终态」：迁移表里存在 `COMPLETED → REFUNDED`，所以 `COMPLETED` 不是状态图的汇点
 * （链 A 品质审查 M6 的用词更正；常量名与 `isOrderClosed` 对齐）。
 *
 * 与 `apps/api/src/modules/shelves/shelves.service.ts` 里原本各写一份的
 * `CLOSED_ORDER_STATUSES` **合并到这里**：同一份「什么算没完成」的语义出现两处，
 * 迟早一处改了另一处没改 —— 而 `SHELF_HAS_OPEN_ORDERS` 正是靠这份集合判断的。
 */
export const CLOSED_ORDER_STATUSES = ['COMPLETED', 'CANCELLED', 'REFUNDED'];
// `satisfies` 而不是 `as readonly OrderStatus[]`：前者保留字面量类型**同时**校验成员合法性，
// 把 'COMPLETE' 这类拼写错误重新变成编译错误（链 A 品质审查 M6）。
/** 已关闭（禁改）：交付完成、已退款或已取消。注意 `COMPLETED → REFUNDED` 存在，
 *  所以这不是状态图的汇点，而是「不再接受写操作」的集合 —— 名字按语义取 `Closed`。 */
export function isOrderClosed(status) {
    return CLOSED_ORDER_STATUSES.includes(status);
}
/**
 * **行级完成度判据**（契约 §12.2）：`pickedQty + shortQty >= quantity`。
 *
 * 这是「一行是否已覆盖」的**唯一实现**（链 D 品质审查 I-2）：`canReachReady`（下单后
 * 的 `READY` 守卫）、`OrderStateService.linesCovered`（pick / confirmPayment / complete）、
 * `OrdersService.status`（顾客端的 `pickedLines`）三处都调它，而不是各自内联一遍。
 * 契约里这段判据原有 9 处散文副本，任何一处漂移（`>` / `===` / 漏掉 `shortQty`）都会造成
 * 「交付门说未完成、状态机说可交付」的分裂，而这几种写法**在正常数据下完全等价** ——
 * 只有异常数据（历史超挑、直接改库）才区分得开，也就是极难靠 review 发现。
 *
 * **为什么必须是 `>=` 而不是 `===`**（附录 A 第 16 条）：挑拣是累计值、没有回退语义，
 * 超挑行（`pickedQty > quantity`）用 `===` 永远算未完成，而客户端再也发不出一个能
 * 「修好」它的请求 ⇒ 订单永久锁死在 `PICKING`。
 */
export function isLineCovered(line) {
    return line.pickedQty + line.shortQty >= line.quantity;
}
/**
 * `READY` 的完成度守卫（契约 §12.2）：**已收款** 且每一行都已覆盖（`isLineCovered`）。
 *
 * 空行集合返回 `false`：没有行就谈不上「可交付」，否则空单会直接跳到 `READY`。
 * 缺货（`shortQty`）算覆盖 —— 否则缺货行会把订单永远卡在 `PICKING`。
 */
export function canReachReady(order) {
    if (order.lines.length === 0)
        return false;
    if (!order.paidConfirmed)
        return false;
    return order.lines.every(isLineCovered);
}
//# sourceMappingURL=order.js.map